import { Dropdown } from "antd";
import { CaretDownOutlined } from "@ant-design/icons";
import {
  useTransform,
  useLayout,
  useDiagram,
  useNotes,
  useAreas,
} from "../hooks";
import { useTranslation } from "react-i18next";
import {
  ZoomInOutlined,
  ZoomOutOutlined,
} from "@ant-design/icons";
import { isRtl } from "../i18n/utils/rtl";
import { useDispatch, useSelector } from "react-redux";
import { editDrawDB } from "ee/actions/drawdbActions";
import { getDrawDBApp } from "ee/selectors/drawdbSelectors";
import { getTenantConfig } from "ee/selectors/tenantSelectors";
import { IconAddTable, IconAddArea, IconAddNote } from "../icons";
import { InputNumber, Tooltip } from "antd";

export default function BottomFloatingControls() {
  const { transform, setTransform } = useTransform();
  const { setLayout } = useLayout();
  const { tables, addTable } = useDiagram();
  const { notes, setNotes, updateNote, addNote } = useNotes();
  const { areas, setAreas, updateArea, addArea } = useAreas();
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const drawDBApp = useSelector(getDrawDBApp);
  const tenantConfig = useSelector(getTenantConfig);

  const fitWindow = () => {
    const diagram = document.getElementById("diagram").getBoundingClientRect();
    const canvas = document.getElementById("canvas").getBoundingClientRect();

    const scaleX = canvas.width / diagram.width;
    const scaleY = canvas.height / diagram.height;
    const scale = Math.min(scaleX, scaleY);
    const translateX = canvas.left;
    const translateY = canvas.top;

    setTransform((prev) => ({
      ...prev,
      zoom: scale - 0.01,
      pan: { x: translateX, y: translateY },
    }));
  };

  return (
    <div
      className="flex gap-2 p-2"
      style={{
        backgroundColor: "rgba(255,255,255, 1)",
        border: "1px solid rgba(68,83,130, 0.25)",
        borderRadius: "10px",
        boxShadow:
          "0px 2px 6px 0px rgba(0,0,0,0.04),0px 4px 12px 0px rgba(0,0,0,0.02)",
        columnGap: "2px",
        height: "40px",
        padding: "0 4px",
        pointerEvents: "auto",
      }}
    >
      <Dropdown
        placement={isRtl(i18n.language) ? "bottomRight" : "bottomLeft"}
        trigger={["click"]}
        menu={{
          style: { 
            width: "240px",
            ...(isRtl(i18n.language) ? { direction: "rtl" } : {})
          },
          items: [
            {
              key: 'fit-window',
              label: (
                <div style={{ display: "flex", justifyContent: "space-between" }} className="hover-2">
                  <div>{t("fit_window_reset")}</div>
                  <div className="text-gray-400">Ctrl+Alt+W</div>
                </div>
              ),
              onClick: fitWindow,
            },
            { type: 'divider' },
            ...[0.25, 0.5, 0.75, 1.0, 1.25, 1.5, 2.0, 3.0].map((e, i) => ({
              key: `zoom-${i}`,
              label: (
                <div className="hover-2">
                  {Math.floor(e * 100)}%
                </div>
              ),
              onClick: () => {
                setTransform((prev) => {
                  dispatch(
                    editDrawDB({
                      id: drawDBApp.id,
                      layout: { ...drawDBApp.layout, zoom: e },
                    }),
                  );
                  return { ...prev, zoom: e };
                });
              },
            })),
            { type: 'divider' },
            {
              key: 'custom-zoom',
              label: (
                <div onClick={(e) => e.stopPropagation()} className="hover-2">
                  <InputNumber
                    placeholder={t("zoom")}
                    suffix="%"
                    value={Math.floor(transform.zoom * 100)}
                    onChange={(v) =>
                      setTransform((prev) => {
                        const zoomValue = parseFloat(v) * 0.01;
                        dispatch(
                          editDrawDB({
                            id: drawDBApp.id,
                            layout: {
                              ...drawDBApp.layout,
                              zoom: zoomValue,
                            },
                          }),
                        );
                        return { ...prev, zoom: zoomValue };
                      })
                    }
                  />
                </div>
              ),
            },
          ],
        }}
      >
        <div className="py-1 px-2 rounded flex items-center justify-center cursor-pointer button-hover">
          <div className="w-[40px]">{Math.floor(transform.zoom * 100)}%</div>
          <div>
            <CaretDownOutlined />
          </div>
        </div>
      </Dropdown>
      <Tooltip title={t("zoom_in")} placement="bottom">
        <button
          className="py-1 px-2 rounded text-lg button-hover"
          onClick={() =>
            setTransform((prev) => {
              dispatch(
                editDrawDB({
                  id: drawDBApp.id,
                  layout: { ...drawDBApp.layout, zoom: prev.zoom * 1.2 },
                }),
              );
              return { ...prev, zoom: prev.zoom * 1.2 };
            })
          }
        >
          <ZoomInOutlined />
        </button>
      </Tooltip>
      <Tooltip title={t("zoom_out")} placement="bottom">
        <button
          className="py-1 px-2 rounded text-lg button-hover"
          onClick={() =>
            setTransform((prev) => {
              dispatch(
                editDrawDB({
                  id: drawDBApp.id,
                  layout: { ...drawDBApp.layout, zoom: prev.zoom / 1.2 },
                }),
              );
              return { ...prev, zoom: prev.zoom / 1.2 };
            })
          }
        >
          <ZoomOutOutlined />
        </button>
      </Tooltip>
      <Tooltip title={t("add_table")} placement="bottom">
        <button
          className="flex items-center py-1 px-2 rounded button-hover"
          onClick={() => addTable()}
        >
          <IconAddTable />
        </button>
      </Tooltip>
      <Tooltip title={t("add_area")} placement="bottom">
        <button
          className="py-1 px-2 rounded flex items-center button-hover"
          onClick={() => addArea()}
        >
          <IconAddArea />
        </button>
      </Tooltip>
      <Tooltip title={t("add_note")} placement="bottom">
        <button
          className="py-1 px-2 rounded flex items-center button-hover"
          onClick={() => addNote()}
        >
          <IconAddNote />
        </button>
      </Tooltip>
    </div>
  );
}
